// GanttConectionSingle.jsx
import React, { useMemo } from 'react';

const PALETA_KOLOROW = [
  '#3f51b5', '#f4a261', '#e63946', '#2a9d8f', '#9c27b0',
  '#e91e63', '#00bcd4', '#ff9800', '#8bc34a', '#795548',
];

const KOLOR_DOMYSLNY = '#888888';
const KOLOR_SINGLE = '#ff4d4f'; 

const czyMajaWspolnyArkusz = (arkuszeA, arkuszeB) => {
  if (!arkuszeA || !arkuszeB) return true;

  const listA = Array.isArray(arkuszeA)
    ? arkuszeA.map(String)
    : String(arkuszeA).split(',').map(s => s.trim()).filter(Boolean);

  const listB = Array.isArray(arkuszeB)
    ? arkuszeB.map(String)
    : String(arkuszeB).split(',').map(s => s.trim()).filter(Boolean);

  if (listA.length === 0 || listB.length === 0) return true;

  return listA.some(arkusz => listB.includes(arkusz));
};

const zawieraElement = (elementyIdText, searchElementId) => {
  if (elementyIdText == null || searchElementId == null) {
    return false;
  }
  const target = String(searchElementId).trim();

  if (Array.isArray(elementyIdText)) {
    return elementyIdText.map(String).includes(target);
  }

  const lista = String(elementyIdText).split(',').map(s => s.trim()).filter(Boolean);
  return lista.includes(target);
};

const safeParseInt = (val) => {
  if (val === null || val === undefined || val === '') return null;
  const parsed = parseInt(val, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const GanttConectionSingle = ({
  groupedStages = {},
  processorIds = [],
  rowYPositions = {},
  minDate,
  maxDate,
  totalChartWidth,
  selectedZamowienieId,
  showSingleConection,
  selectedStageId,
  selectedGrup,
  calculateBarProperties
}) => {
  const connectionLines = useMemo(() => {
    if (!showSingleConection) return [];
    
    const selectedStageInt = safeParseInt(selectedStageId);
    if (selectedStageInt === null) return [];

    if (!minDate || !maxDate || !totalChartWidth || typeof calculateBarProperties !== 'function') {
      return [];
    }

    const selectedZamowienieInt = safeParseInt(selectedZamowienieId);
    const targetElementIdFromGrup = selectedGrup 
      ? safeParseInt(selectedGrup.element_id || selectedGrup.elementId)
      : null;

    // Zmiana: przechowujemy TABLICĘ kafelków pod danym indeksem!
    const stagesByElementAndIndex = {};
    const elementColorMap = {};
    const targetStages = [];
    const oprawaStages = [];
    let targetStageObj = null;

    let processedStagesCount = 0;

    // 1. Zbieranie wszystkich etapów zamówienia
    processorIds.forEach((processorId) => {
      const rowStages = groupedStages[processorId] || [];
      // const y = rowYPositions[processorId] ?? 0;
      

      rowStages.forEach((stage) => {
        if (!stage || stage.zamowienie_id == null) return;

        const zamowienieIdInt = safeParseInt(stage.zamowienie_id);
        if (selectedZamowienieInt !== null && zamowienieIdInt !== selectedZamowienieInt) return;

        processedStagesCount++;

        const stageIdInt = safeParseInt(stage.global_id || stage.id);
        const stageRawIdInt = safeParseInt(stage.id);
        const elementIdInt = safeParseInt(stage.element_id);
        const procesNazwaId = safeParseInt(stage.proces_nazwa_id);
        const indeks = safeParseInt(stage.indeks_procesu ?? stage.indeks ?? stage.krok) ?? 0;

        if (elementIdInt !== null && !(elementIdInt in elementColorMap)) {
          const currentIndex = Object.keys(elementColorMap).length;
          elementColorMap[elementIdInt] = currentIndex < PALETA_KOLOROW.length
            ? PALETA_KOLOROW[currentIndex]
            : KOLOR_DOMYSLNY;
        }

        const barProps = calculateBarProperties(stage.start, stage.end);
        const leftPx = Number(barProps?.leftPx);
        const widthPx = Number(barProps?.widthPx);

        if (Number.isNaN(leftPx) || Number.isNaN(widthPx)) return;
        const y = stage._centerY ?? rowYPositions[processorId] ?? 0;

        const stageObj = {
          ...stage,
          stageId: stageIdInt,
          stageRawId: stageRawIdInt,
          zamowienieId: zamowienieIdInt,
          elementId: elementIdInt,
          elementyIdText: stage.elementy_id,
          arkuszeId: stage.arkusze_id || stage.arkusz_id || stage.arkusze,
          indeksProcesu: indeks,
          procesNazwaId,
          outPoint: { x: leftPx + widthPx, y },
          inPoint: { x: leftPx, y }
        };

        targetStages.push(stageObj);

        // Precyzyjne wykrycie klikniętego kafelka
        const matchesId = (stageIdInt === selectedStageInt || stageRawIdInt === selectedStageInt);
        const matchesElement = targetElementIdFromGrup === null || (elementIdInt === targetElementIdFromGrup);

        if (matchesId && matchesElement) {
          targetStageObj = stageObj;
        }

        if (procesNazwaId === 6) {
          oprawaStages.push(stageObj);
        } else if (elementIdInt !== null) {
          if (!stagesByElementAndIndex[elementIdInt]) {
            stagesByElementAndIndex[elementIdInt] = {};
          }
          if (!stagesByElementAndIndex[elementIdInt][indeks]) {
            stagesByElementAndIndex[elementIdInt][indeks] = [];
          }
          // Dodajemy do tablicy, aby nie nadpisać rozdzielonych grup!
          stagesByElementAndIndex[elementIdInt][indeks].push(stageObj);
        }
      });
    });

    if (!targetStageObj) {
      targetStageObj = targetStages.find(s => 
        s.stageId === selectedStageInt || s.stageRawId === selectedStageInt
      );
    }

    if (processedStagesCount === 0 || !targetStageObj) return [];

    const lines = [];

    // 2. KLIKNIĘTY STAGE JEST PROCESEM ELEMENTU (NIE OPRAWĄ)
    if (targetStageObj.procesNazwaId !== 6 && targetStageObj.elementId !== null) {
      const elementMap = stagesByElementAndIndex[targetStageObj.elementId] || {};
      const currentIndeks = targetStageObj.indeksProcesu;

      const prevStages = elementMap[currentIndeks - 1] || [];
      const nextStages = elementMap[currentIndeks + 1] || [];

      // A. Rysowanie linii OD WSZYSTKICH pasujących poprzedników (indeks - 1) DO klikniętego stage
      prevStages.forEach((prevStage) => {
        if (czyMajaWspolnyArkusz(prevStage.arkuszeId, targetStageObj.arkuszeId)) {
          lines.push({
            id: `single_prev_${prevStage.stageId}_to_${targetStageObj.stageId}`,
            elementId: targetStageObj.elementId,
            from: prevStage.outPoint,
            to: targetStageObj.inPoint,
            strokeColor: KOLOR_SINGLE,
            isOprawa: false
          });
        }
      });

      // B. Rysowanie linii OD klikniętego stage DO WSZYSTKICH pasujących następników (indeks + 1)
      let drawnToNext = false;

      nextStages.forEach((nextStage) => {
        if (czyMajaWspolnyArkusz(targetStageObj.arkuszeId, nextStage.arkuszeId)) {
          drawnToNext = true;
          lines.push({
            id: `single_next_${targetStageObj.stageId}_to_${nextStage.stageId}`,
            elementId: targetStageObj.elementId,
            from: targetStageObj.outPoint,
            to: nextStage.inPoint,
            strokeColor: KOLOR_SINGLE,
            isOprawa: false
          });
        }
      });

      // C. Jeśli nie ma żadnego pasującego następnika w tym elemencie -> połączenie do oprawy
      if (!drawnToNext && nextStages.length === 0) {
        const matchingOprawy = oprawaStages.filter(oprawaStage =>
          zawieraElement(oprawaStage.elementyIdText, targetStageObj.elementId)
        );

        const strokeColor = elementColorMap[targetStageObj.elementId] || KOLOR_SINGLE;

        matchingOprawy.forEach((oprawaStage) => {
          lines.push({
            id: `single_oprawa_${targetStageObj.stageId}_to_${oprawaStage.stageId}`,
            elementId: targetStageObj.elementId,
            from: targetStageObj.outPoint,
            to: oprawaStage.inPoint,
            strokeColor: strokeColor,
            isOprawa: true
          });
        });
      }

    } else if (targetStageObj.procesNazwaId === 6) {
      // 3. OPRAWA (procesNazwaId === 6)
      Object.keys(stagesByElementAndIndex).forEach((elemIdStr) => {
        const elemId = safeParseInt(elemIdStr);
        if (!zawieraElement(targetStageObj.elementyIdText, elemId)) return;

        const elementMap = stagesByElementAndIndex[elemId];
        const indexes = Object.keys(elementMap).map(Number);
        if (indexes.length === 0) return;

        const maxIndeks = Math.max(...indexes);
        const lastStages = elementMap[maxIndeks] || [];

        lastStages.forEach((lastStage) => {
          const strokeColor = elementColorMap[elemId] || KOLOR_DOMYSLNY;

          lines.push({
            id: `single_oprawa_part_${elemId}_${lastStage.stageId}_to_${targetStageObj.stageId}`,
            elementId: elemId,
            from: lastStage.outPoint,
            to: targetStageObj.inPoint,
            strokeColor: strokeColor,
            isOprawa: true
          });
        });
      });
    }

    return lines;
  }, [
    groupedStages,
    processorIds,
    rowYPositions,
    minDate,
    maxDate,
    totalChartWidth,
    selectedZamowienieId,
    showSingleConection,
    selectedStageId,
    selectedGrup,
    calculateBarProperties
  ]);

  if (connectionLines.length === 0) return null;

  const uniqueElements = Array.from(
    new Map(
      connectionLines
        .filter(line => line.elementId !== null)
        .map(line => [line.elementId, line.strokeColor])
    ).entries()
  );

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9
      }}
    >
      <defs>
        {uniqueElements.map(([elementId, color]) => (
          <marker
            key={`marker-single-${elementId}`}
            id={`arrowhead-single-${elementId}`}
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 6 3, 0 6" fill={color} />
          </marker>
        ))}
      </defs>

      {connectionLines.map((line) => {
        const { from, to, id, strokeColor, elementId, isOprawa } = line;
        const deltaX = Math.max(20, Math.abs(to.x - from.x) / 2);
        const pathData = `M ${from.x} ${from.y} C ${from.x + deltaX} ${from.y}, ${to.x - deltaX} ${to.y}, ${to.x} ${to.y}`;
        const markerId = elementId !== null ? `url(#arrowhead-single-${elementId})` : undefined;

        return (
          <g key={`connection-single-${id}`}>
            <path
              d={pathData}
              fill="none"
              stroke={strokeColor}
              strokeWidth="2.5"
              strokeDasharray={isOprawa ? "6 2" : "6 2"}
              markerEnd={markerId}
            />
            <circle cx={from.x} cy={from.y} r="4" fill={strokeColor} />
            <circle cx={to.x} cy={to.y} r="4" fill={strokeColor} />
          </g>
        );
      })}
    </svg>
  );
};

export default React.memo(GanttConectionSingle);